import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import EditIcon from '@/assets/icons/edit.svg';
import { useTrainingGoals } from '@/features/calendar/hooks/useTrainingGoals';
import { ProgressBelt } from './ProgressBelt';
import { TrainingGoalModal } from './TrainingGoalModal';
import { format, addDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';
import { useI18n } from '@/contexts/I18nContext';

export const TrainingGoalSection: React.FC = () => {
  const { user } = useAuth();
  const { goal, isLoading, beltColors, checkGoalProgress } = useTrainingGoals();
  const { profile } = useUserProfile();
  const { t, currentLanguage } = useI18n();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Check goal progress automatically when component mounts (only once)
  useEffect(() => {
    if (goal && user?.id) {
      checkGoalProgress().catch(error => {
        console.error('❌ Error checking goal progress:', error);
      });
    }
  }, [goal?.id, user?.id]); // Remove checkGoalProgress from dependencies to prevent infinite loop



  // Get user's belt color from profile
  const getUserBeltColor = (): string => {
    if (!profile?.belt_rank) return 'white';
    
    // Map belt_rank to our BeltColor type
    const beltRank = profile.belt_rank.toLowerCase();
    if (beltRank.includes('white')) return 'white';
    if (beltRank.includes('blue')) return 'blue';
    if (beltRank.includes('purple')) return 'purple';
    if (beltRank.includes('brown')) return 'brown';
    if (beltRank.includes('black')) return 'black';
    
    return 'white'; // default
  };

  const handleOpenModal = (editing: boolean = false) => {
    setIsEditing(editing);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
  };

  // Helper function for pluralization
  const getPlural = (count: number, language: 'pt' | 'en'): string => {
    if (language === 'pt') {
      return count === 1 ? '' : 's';
    } else {
      return count === 1 ? '' : 's';
    }
  };

  // Helper function for pluralization of "vez" (special case in Portuguese)
  const getVezPlural = (count: number, language: 'pt' | 'en'): string => {
    if (language === 'pt') {
      return count === 1 ? 'vez' : 'vezes';
    } else {
      return count === 1 ? 'time' : 'times';
    }
  };

  // Helper function for "Falta" vs "Faltam" (Portuguese only - English doesn't need this)
  const getRemainingVerb = (count: number, language: 'pt' | 'en'): string => {
    if (language === 'pt') {
      return count === 1 ? 'Falta' : 'Faltam';
    } else {
      return ''; // English doesn't use this verb
    }
  };

  const getProgressMessage = () => {
    if (!goal) return '';

    const { current_week_progress, weekly_goal, is_monthly_goal_met } = goal;

    if (is_monthly_goal_met) {
      return t('home.training_goal.monthly_goal_achieved');
    }

    // Check if weekly goal is already met
    const weeklyGoalMet = current_week_progress >= weekly_goal;
    
    if (weeklyGoalMet) {
      // Goal already met - celebratory message without (X/Y)
      const vezWord = getVezPlural(current_week_progress, currentLanguage);
      return t('home.training_goal.weekly_goal_met_message', {
        current: current_week_progress,
        vez_word: vezWord
      });
    }

    // Goal not met yet - show progress with (X/Y) and encouraging message
    const remaining = weekly_goal - current_week_progress;
    const currentPlural = getPlural(current_week_progress, currentLanguage);
    const remainingVerb = currentLanguage === 'pt' ? getRemainingVerb(remaining, currentLanguage) : '';
    
    return t('home.training_goal.weekly_progress_message', { 
      current: current_week_progress, 
      goal: weekly_goal,
      remaining: remaining,
      current_plural: currentPlural,
      remaining_verb: remainingVerb
    });
  };

  const getWeeklyMessage = () => {
    if (!goal) return '';
    
    const { current_week_progress, weekly_goal } = goal;
    
    // Check if weekly goal is already met
    const weeklyGoalMet = current_week_progress >= weekly_goal;
    
    if (weeklyGoalMet) {
      // Goal already met - celebratory message
      const vezWord = getVezPlural(current_week_progress, currentLanguage);
      return t('home.training_goal.weekly_goal_met_celebration', {
        current: current_week_progress,
        goal: weekly_goal,
        vez_word: vezWord
      });
    }

    // Goal not met yet - show progress with (X/Y) and encouraging message
    const remaining = weekly_goal - current_week_progress;
    const goalPlural = getPlural(weekly_goal, currentLanguage);
    const currentPlural = getPlural(current_week_progress, currentLanguage);
    
    return t('home.training_goal.weekly_goal_message', { 
      goal: weekly_goal, 
      current: current_week_progress || 0,
      remaining: remaining,
      goal_plural: goalPlural,
      current_plural: currentPlural
    });
  };

  const getDateRange = () => {
    if (!goal) return '';
    
    // ✅ LÓGICA CORRETA: mostrar o mês atual (não o ciclo da meta)
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return `${format(monthStart, 'dd MMM')} to ${format(monthEnd, 'dd MMM')}`;
  };

  const weeklyGoalsStatus = useMemo(() => {
    if (!goal) return [false, false, false, false];
    
    // ✅ LÓGICA CORRETA: graus baseados no progresso da semana atual
    const { current_week_progress, weekly_goal, total_completed, monthly_goal } = goal;
    
    // Grau 1: Semana atual completada
    const week1Completed = current_week_progress >= weekly_goal;
    
    // Grau 2: Duas semanas completadas (seguindo o padrão de 4 semanas por mês)
    const week2Completed = total_completed >= weekly_goal * 2;
    
    // Grau 3: Três semanas completadas
    const week3Completed = total_completed >= weekly_goal * 3;
    
    // Grau 4: Meta mensal atingida
    const week4Completed = total_completed >= monthly_goal;
    
    return [week1Completed, week2Completed, week3Completed, week4Completed];
  }, [goal?.current_week_progress, goal?.weekly_goal, goal?.total_completed, goal?.monthly_goal]);

  if (isLoading) {
    return (
      <div className="bg-[#2d2424] rounded-[10px] h-[129px] w-[350px] p-6 flex items-center justify-center">
        <div className="text-white">{t('common.loading')}</div>
      </div>
    );
  }

  if (!goal) {
    // Empty State - Following Figma design exactly
    return (
      <>
        <div className="bg-[#2d2424] h-[130px] w-full rounded-lg p-4 flex flex-col items-center justify-center text-center relative">
          {/* Main title - "No goal set yet!" */}
          <h3 className="text-[14px] font-bold text-white font-roboto mb-2">
            {t('home.training_goal.no_goal_set')}
          </h3>
          
          {/* Subtitle - "Track your progress and earn a new stripe by week completed." */}
          <p className="text-[10px] font-light text-white mb-4 max-w-[287px]">
            {t('home.training_goal.no_goal_description')}
          </p>
          
          {/* Button - "Set goal" with plus icon */}
          <Button
            onClick={() => handleOpenModal(false)}
            className="bg-transparent border-white border text-white h-5 px-[30px] py-2 rounded-[100px] text-[10px] font-bold"
          >
            <Plus className="w-3.5 h-3.5 mr-2" />
            {t('home.training_goal.set_goal')}
          </Button>
        </div>

        {/* Modal for empty state */}
        <TrainingGoalModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isEditing={isEditing}
          currentGoal={undefined}
        />
      </>
    );
  }

  // Active State with Progress
  return (
    <div className="w-full">
      {/* Title and Edit Button - Above the rectangle */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-bold text-white">{t('home.training_goal.monthly_progress')}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleOpenModal(true)}
            className="text-white/60 hover:text-white"
          >
            <img src={EditIcon} alt="Edit" width={16} height={16} style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>

      {/* Weekly Message - Above the rectangle */}
      <p className="text-[10px] font-light text-white mb-3">
        {getWeeklyMessage()}
      </p>

      {/* Main Progress Container */}
      <div className="bg-[#2d2424] rounded-lg w-full p-4 relative">
        {/* Date Range and Progress Counter */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-light text-white">{getDateRange()}</span>
          <span className="text-[12px] font-normal text-white">
            {goal.total_completed}/{goal.monthly_goal}
          </span>
        </div>

        {/* Progress Belt - Posicionada entre os textos */}
        <div className="flex justify-center mb-3">
          <ProgressBelt
            beltColor={getUserBeltColor() as any}
            progress={goal.total_completed || 0}
            total={goal.monthly_goal || 1}
            weeklyGoals={weeklyGoalsStatus}
            className="w-full max-w-[316px]"
          />
        </div>

        {/* Progress Status Message */}
        <p className="text-[12px] font-normal text-white text-center">
          {getProgressMessage()}
        </p>
      </div>

      {/* Modal */}
      <TrainingGoalModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isEditing={isEditing}
        currentGoal={goal.monthly_goal}
      />
    </div>
  );
};
